import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonContainer, ContentSection, Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Formik, FormikContextType, useFormikContext } from 'formik';
import { ObjectSchema } from 'yup';
import { CountryCodesEnum, frenchTerritoriesOptions } from '../../../../resources/base/models/country';
import { CustomerDetailsSection } from '../../../../resources/base/models/enums';
import {
  CitiesAndZipCodesSuggestion,
  localitiesWithZipCodes,
  zipCodesForLocality,
} from '../../../../services/api/address/localitiesAndZipCodes';
import { DeepRequired } from '../../../../services/common/miscUtils.types';
import {
  creditApplicationSelectors,
  setFormAsNotSubmitted,
} from '../../../../services/redux/features/creditApplication.slice';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { useNavigate } from '../../../../services/routing/navigate';
import {
  CeoBirthCountry,
  CeoBirthName,
  CeoBirthPlaceCity,
  CeoBirthPlaceZip,
  CeoDateOfBirth,
  CeoFirstName,
  CeoLastName,
  CeoNationality,
  Gender,
} from '../../utils/fieldsBusinessCustomer';
import { formatCityNameLettersAndSpaceOnly } from '../../utils/formatCityString';
import {
  CustomerDetailsFormGroupForms,
  CustomerDetailsFormGroupType,
  DisabledCreditApplicationBirthInformationFields,
  patchCreditApplicationAndUpdateState,
  useFormSectionHelpers,
} from '../utils';
import { getPersonalInfoDataFormInitialValues, PersonalInfoDataFormType } from './initialValues';
import { personalDataFormRequestMapper } from './requestMapper';
import { getPersonalInfoDataFormValidationSchema } from './validationSchema';

interface Props {
  formName: CustomerDetailsFormGroupForms<'business'>;
  formGroup: CustomerDetailsFormGroupType;
}

export const PersonalInfoDataForm: FunctionComponent<Props> = ({ formName, formGroup }) => {
  const [initialValues, setInitialValues] = useState({} as PersonalInfoDataFormType);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<PersonalInfoDataFormType>>();
  const creditApplicantCustomerData = useSelector(creditApplicationSelectors.selectCreditApplicantCustomerData) as
    | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse
    | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    setInitialValues(getPersonalInfoDataFormInitialValues(creditApplicantCustomerData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditApplicantCustomerData]);

  useEffect(() => {
    setValidationSchema(getPersonalInfoDataFormValidationSchema(t));
  }, [t]);

  const { formSectionProps, onFormSubmitSuccess, onFormInitialValuesChange } = useFormSectionHelpers(
    formGroup,
    formName
  );

  // Fields must remain editable if the API provides no value; otherwise, the user cannot change the input.
  const disabledFields: DisabledCreditApplicationBirthInformationFields = useMemo(() => {
    const relation = creditApplicantCustomerData?.customerRelations?.[0];
    return {
      nationality: Boolean(relation?.nationality),
      birthName: Boolean(relation?.birthName),
      birthCountry: Boolean(relation?.countryOfBirth),
      zipCodeOfBirth: Boolean(relation?.placeOfBirthZipCode),
      placeOfBirth: Boolean(relation?.placeOfBirth),
    };
  }, [creditApplicantCustomerData]);

  const onSubmit = async (values: PersonalInfoDataFormType) => {
    dispatch(loadingActions.setLoading(true));

    try {
      await patchCreditApplicationAndUpdateState(
        personalDataFormRequestMapper(values as DeepRequired<PersonalInfoDataFormType>, creditApplicantCustomerData)
      );
    } catch {
      navigate.error();
      return;
    } finally {
      dispatch(loadingActions.setLoading(false));
    }

    onFormSubmitSuccess();
  };

  const isCreditApplicationDataReady = useSelector(creditApplicationSelectors.selectIsCreditApplicationDataReady);

  return (
    <ContentSection.ComponentWrapper>
      <FormSection title={t('step4:heading:executiveInformation')} {...formSectionProps}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {isCreditApplicationDataReady && (
            /* eslint-disable-next-line no-use-before-define */
            <FormContent onFormInitialValuesChange={onFormInitialValuesChange} disabledFields={disabledFields} />
          )}
        </Formik>
      </FormSection>
    </ContentSection.ComponentWrapper>
  );
};

interface FormContentProps {
  onFormInitialValuesChange: (validateForm: FormikContextType<any>['validateForm']) => void;
  disabledFields: DisabledCreditApplicationBirthInformationFields;
}

const FormContent: FunctionComponent<FormContentProps> = ({ onFormInitialValuesChange, disabledFields }) => {
  const { t } = useTranslation();
  const { values, setFieldValue, submitForm, initialValues, validateForm, dirty } =
    useFormikContext<PersonalInfoDataFormType>();

  const [suggestionsForPlacefBirth, setSuggestionsForPlaceOfBirth] = useState<CitiesAndZipCodesSuggestion[]>([]);
  const [suggestedLocationsLoading, setSuggestedLocationsLoading] = useState(false);
  const [suggestionsForZipCodeOfBirth, setSuggestionsForZipCodeOfBirth] = useState<CitiesAndZipCodesSuggestion[]>([]);
  const [suggestedZipCodesLoading, setSuggestedZipCodesLoading] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const dispatch = useDispatch();
  const isBornInFrance = values.ceoBirthCountry === CountryCodesEnum.FR;
  const isFrenchTerritory = frenchTerritoriesOptions.some((option) => option.value === values.ceoBirthCountry);

  useEffect(() => {
    onFormInitialValuesChange(validateForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    // When any form field changes after submit, user is forced to submit the form again
    if (dirty) {
      dispatch(setFormAsNotSubmitted(CustomerDetailsSection.PERSONAL_INFO));
    }
  }, [dirty, dispatch]);

  const onSelectSuggestion = (suggestion: string) => {
    const zipMatch = suggestion.match(/\b\d{5}\b/);
    const zip = zipMatch ? zipMatch[0] : null;
    const city = zip ? suggestion.replace(zip, '').trim() : suggestion.trim();

    setIsUserTyping(false);
    if (isFrenchTerritory) {
      setFieldValue('ceoBirthPlaceZip', zip);
    }
    setFieldValue('ceoBirthPlaceCity', formatCityNameLettersAndSpaceOnly(city));
  };

  const onZipCodeOfBirthChange = (value: string) => {
    setIsUserTyping(true);
    setFieldValue('ceoBirthPlaceZip', value);
  };

  const onPlaceOfBirthChange = (value: string) => {
    setIsUserTyping(true);
    setFieldValue('ceoBirthPlaceCity', value);
  };

  useEffect(() => {
    if (!values.ceoBirthPlaceZip || values.ceoBirthPlaceZip?.length < 5) return;

    async function fetchSuggestions() {
      try {
        setSuggestedZipCodesLoading(true);
        const res = await localitiesWithZipCodes(values.ceoBirthPlaceZip || '');
        setSuggestionsForZipCodeOfBirth(res);
        setSuggestionsForPlaceOfBirth([]);
      } catch {
        setSuggestedZipCodesLoading(false);
      } finally {
        setSuggestedZipCodesLoading(false);
      }
    }

    fetchSuggestions();
  }, [values.ceoBirthPlaceZip]);

  useEffect(() => {
    if (!values.ceoBirthPlaceCity || values.ceoBirthPlaceCity?.length < 3) return;

    async function fetchSuggestions() {
      try {
        setSuggestedLocationsLoading(true);
        const res = await zipCodesForLocality(values.ceoBirthPlaceCity || '');
        setSuggestionsForPlaceOfBirth(res);
        setSuggestionsForZipCodeOfBirth([]);
      } catch {
        setSuggestedLocationsLoading(false);
      } finally {
        setSuggestedLocationsLoading(false);
      }
    }

    const handler = setTimeout(() => {
      fetchSuggestions();
    }, 1000);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(handler);
    };
  }, [values.ceoBirthPlaceCity]);

  const isSoleTrader = true;

  return (
    <Fieldset className="u-pt-none">
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <Gender t={t} isDisabled={isSoleTrader} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoFirstName t={t} isDisabled={isSoleTrader} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoLastName t={t} isDisabled={isSoleTrader} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoBirthName t={t} isDisabled={isSoleTrader && disabledFields.birthName} />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/3" s="1/1">
            <CeoDateOfBirth
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              label={t('step4:ceoDateOfBirth:label')}
              infoText={t('step4:dateInfoText:label')}
              isDisabled={isSoleTrader}
            />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/3" s="1/1">
            <CeoNationality
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              isDisabled={isSoleTrader && disabledFields.nationality}
            />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/3" s="1/1">
            <CeoBirthCountry
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              isDisabled={isSoleTrader && disabledFields.birthCountry}
            />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/3" s="1/1">
            <CeoBirthPlaceZip
              t={t}
              required={isBornInFrance}
              isDisabled={!isFrenchTerritory || (isSoleTrader && disabledFields.zipCodeOfBirth)}
              suggestions={suggestionsForZipCodeOfBirth}
              onChange={onZipCodeOfBirthChange}
              onSelectSuggestion={onSelectSuggestion}
              isLoading={suggestedZipCodesLoading}
            />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/3" s="1/1">
            <CeoBirthPlaceCity
              t={t}
              required={isBornInFrance}
              onChange={onPlaceOfBirthChange}
              onSelectSuggestion={onSelectSuggestion}
              isLoading={suggestedLocationsLoading}
              suggestions={isUserTyping ? suggestionsForPlacefBirth : []}
              isDisabled={isSoleTrader && disabledFields.placeOfBirth}
            />
          </Layout.Item>
        </Layout>
        <br />
        <ButtonContainer right>
          <Layout right>
            <Layout.Item default="1/3" m="2/5" s="2/3">
              <Button full type="submit" onClick={submitForm}>
                {t('step3:navigation:continue')}
              </Button>
            </Layout.Item>
          </Layout>
        </ButtonContainer>
      </Fieldset.Row>
    </Fieldset>
  );
};
